import { permissionKeyFor } from '@/stores/entityStore';
import type { Gate } from '@/navigation/gates';
import { TRACK_MATCHING_ENTITY_ID } from '@/composables/pageLevelTableEntities';
import { AUDIO_FILE_ISSUES_ENTITY_ID } from '@/composables/audioFileIssuesSchema';
import { PLAYLIST_SWITCHING_ENTITY_ID } from '@/composables/playlistSwitchingSchema';

/**
 * The only place that says which area of the app an entity belongs to.
 *
 * The icon strip, the section sidebars, the list and record routes, the
 * breadcrumbs, Cmd+K's tabs and where an imported shared view lands are all
 * built from this table. A unit test fails if an entity with a list sits in
 * no area, or in two.
 */

export type AreaId = 'home' | 'clients' | 'players' | 'music' | 'reports' | 'settings';

export interface AreaEntity {
    /** The models.json id, also the key saved views store, for example `LocationZonePlayer`. */
    entityId: string;
    /** The address segment under the area, for example `zones` for `/clients/zones`. */
    segment: string;
    /**
     * The sidebar and breadcrumb label, for example "All players". Search names a
     * record type by its plural noun instead, "Players", next to the other types.
     */
    label: string;
    /** The singular noun on a record page, for example "Player". */
    noun: string;
}

export interface AreaPage {
    label: string;
    path: string;
    routeName: string;
    gate: Gate | null;
    /**
     * The entity id this page's saved views are stored under, for a page that
     * has saved views of its own, such as `TrackMatching`. Those views are
     * listed in the area's sidebar beside its lists' views, and open on this
     * page with `?view=<id>`.
     */
    savedViewEntityId?: string;
}

export interface Area {
    id: AreaId;
    label: string;
    /**
     * The area's own address prefix. Players is the one area with a single entity,
     * so its list sits at `/players` rather than `/players/<segment>`.
     */
    pathPrefix: string;
    entities: AreaEntity[];
    /** Pages listed in the sidebar, after the area's record types. */
    pages: AreaPage[];
    /**
     * A heading over the pages, set apart from the record types, as Music's
     * "Tools". Without one, the pages carry on the same list as the record
     * types, as in Reports, where Live track actions, Audio file issues,
     * Switches and Dynamo snapshots read as one list (Joel, 2026-10-17).
     */
    pagesHeading?: string;
}

/**
 * Searchable record types left out of AREAS on purpose, so they have no sidebar
 * link, list or Create New entry. Leaving them out of AREAS is what does that.
 * This set only records the choice, so the test that every searchable type has
 * an area can tell an omission from a mistake. Music zone folders wait for an
 * icon of their own (Joel, 2026-10-13).
 */
export const ENTITIES_LEFT_OUT_FOR_NOW: ReadonlySet<string> = new Set(['MusicZoneFolder']);

export const AREAS: readonly Area[] = [
    { id: 'home', label: 'Home', pathPrefix: '/', entities: [], pages: [] },
    {
        id: 'clients',
        label: 'Clients',
        pathPrefix: '/clients',
        entities: [
            { entityId: 'Account', segment: 'accounts', label: 'Accounts', noun: 'Account' },
            { entityId: 'Location', segment: 'locations', label: 'Locations', noun: 'Location' },
            { entityId: 'LocationZone', segment: 'zones', label: 'Zones', noun: 'Zone' },
            { entityId: 'Contact', segment: 'contacts', label: 'Contacts', noun: 'Contact' },
        ],
        pages: [],
    },
    {
        id: 'players',
        label: 'Players',
        pathPrefix: '/players',
        entities: [
            { entityId: 'LocationZonePlayer', segment: '', label: 'All players', noun: 'Player' },
        ],
        pages: [],
    },
    {
        id: 'music',
        label: 'Music',
        pathPrefix: '/music',
        pagesHeading: 'Tools',
        entities: [
            { entityId: 'Bucket', segment: 'buckets', label: 'Buckets', noun: 'Bucket' },
            { entityId: 'LocationZoneCustomPlaylist', segment: 'custom-playlists', label: 'Custom playlists', noun: 'Custom playlist' },
            { entityId: 'ContactLoginCustomPlaylist', segment: 'contact-custom-playlists', label: 'Contact custom playlists', noun: 'Contact custom playlist' },
            { entityId: 'LiveTrack', segment: 'live-tracks', label: 'Live tracks', noun: 'Live track' },
            { entityId: 'Artist', segment: 'artists', label: 'Artists', noun: 'Artist' },
            { entityId: 'Album', segment: 'albums', label: 'Albums', noun: 'Album' },
            { entityId: 'RecordLabel', segment: 'record-labels', label: 'Record labels', noun: 'Record label' },
            { entityId: 'ContactLoginStation', segment: 'stations', label: 'Stations', noun: 'Station' },
            { entityId: 'RawDdexTrack', segment: 'ddex-tracks', label: 'DDEX tracks', noun: 'DDEX track' },
        ],
        pages: [
            {
                label: 'Track matching',
                path: '/track-matching',
                routeName: 'track-matching',
                gate: { kind: 'page', pageName: 'Track Matching' },
                savedViewEntityId: TRACK_MATCHING_ENTITY_ID,
            },
        ],
    },
    {
        id: 'reports',
        label: 'Reports',
        pathPrefix: '/reports',
        // Every live track action across all tracks. One track's actions are also
        // its Actions tab. Not in quick search (Joel, 2026-10-17).
        entities: [
            { entityId: 'LiveTrackAction', segment: 'live-track-actions', label: 'Live track actions', noun: 'Live track action' },
        ],
        pages: [
            {
                label: 'Audio file issues',
                path: '/audio-file-issues',
                routeName: 'audio-file-issues',
                gate: { kind: 'page', pageName: 'Audio File Issues' },
                savedViewEntityId: AUDIO_FILE_ISSUES_ENTITY_ID,
            },
            // Labelled "Switches"; the route and its page grant are still called Playlists.
            {
                label: 'Switches',
                path: '/reports/playlist-switching',
                routeName: 'playlist-switching',
                gate: { kind: 'page', pageName: 'Playlists' },
                savedViewEntityId: PLAYLIST_SWITCHING_ENTITY_ID,
            },
            { label: 'Dynamo snapshots', path: '/dynamo-table-stats', routeName: 'dynamo-table-stats', gate: { kind: 'developer' } },
        ],
    },
    {
        id: 'settings',
        label: 'Settings',
        pathPrefix: '/settings',
        entities: [],
        pages: [
            { label: 'Permissions', path: '/permissions', routeName: 'permissions', gate: { kind: 'model', ability: 'view', permissionKey: 'permission_management' } },
            { label: 'DDEX', path: '/ddex', routeName: 'ddex-configurations', gate: { kind: 'developer' } },
            { label: 'Profile', path: '/profile', routeName: 'profile', gate: null },
        ],
    },
];

const entityIndex = new Map<string, { area: Area; entity: AreaEntity }>(
    AREAS.flatMap((area) => area.entities.map((entity) => [entity.entityId, { area, entity }] as const)),
);

/**
 * The page a route belongs to, by its name or by sitting under the page's
 * address, such as `/audio-file-issues/E1` under Audio file issues. Null for a
 * route that is not one of the areas' pages.
 */
export function pageForRoute(routeName: string, path: string): AreaPage | null {
    for (const area of AREAS) {
        const page = area.pages.find((candidate) => candidate.routeName === routeName || path.startsWith(`${candidate.path}/`));
        if (page) return page;
    }
    return null;
}

/**
 * True when the area has saved views: it has a record type, or a page with
 * saved views of its own, such as Track matching. The sidebar's Saved views
 * and Shared views sections, and the phone's two pills for them, show only
 * then.
 */
export function areaHasSavedViews(area: Area): boolean {
    return area.entities.length > 0 || area.pages.some((page) => page.savedViewEntityId !== undefined);
}

export function areaForEntity(entityId: string): Area | null {
    return entityIndex.get(entityId)?.area ?? null;
}

/**
 * A record noun as it reads mid-sentence: "Player" becomes "player", and an
 * acronym keeps its capitals, so "DDEX track" stays "DDEX track".
 */
export function nounInSentence(noun: string): string {
    return noun
        .split(' ')
        .map((word) => (word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()))
        .join(' ');
}

export function areaEntity(entityId: string): AreaEntity | null {
    return entityIndex.get(entityId)?.entity ?? null;
}

export function entityGate(entityId: string): Gate {
    return { kind: 'entity', permissionKey: permissionKeyFor(entityId) };
}

/** The list address, for example `/clients/zones`, or `/players`. */
export function listPathFor(entityId: string): string {
    const found = entityIndex.get(entityId);
    if (!found) {
        throw new Error(`No area holds the entity "${entityId}".`);
    }
    return found.entity.segment === '' ? found.area.pathPrefix : `${found.area.pathPrefix}/${found.entity.segment}`;
}

/**
 * The area page whose saved views are stored under this entity id, such as
 * Track matching for `TrackMatching`, or null.
 */
export function pageForSavedViewEntity(entityId: string): { area: Area; page: AreaPage } | null {
    for (const area of AREAS) {
        const page = area.pages.find((candidate) => candidate.savedViewEntityId === entityId);
        if (page) return { area, page };
    }
    return null;
}

/**
 * Whether the user may open a saved view of this entity. A list's view needs
 * the entity's permission. A page's view, such as a Track matching view, needs
 * the page's grant. A view that belongs nowhere cannot be opened.
 */
export function savedViewIsOpenable(entityId: string, gateIsOpen: (gate: Gate) => boolean): boolean {
    if (entityIndex.has(entityId)) {
        return gateIsOpen(entityGate(entityId));
    }
    const found = pageForSavedViewEntity(entityId);
    return found !== null && (found.page.gate === null || gateIsOpen(found.page.gate));
}

/**
 * A saved view's address with `?view=<id>`. A list's view opens on its list,
 * for example `/players?view=12`. A page's view opens on its page, for example
 * `/track-matching?view=31`. Null for a view that belongs to neither, which
 * callers skip rather than let listPathFor throw.
 */
export function viewPathFor(entityId: string, viewId: number | string): { path: string; query: { view: string } } | null {
    if (entityIndex.has(entityId)) {
        return { path: listPathFor(entityId), query: { view: String(viewId) } };
    }
    const found = pageForSavedViewEntity(entityId);
    return found ? { path: found.page.path, query: { view: String(viewId) } } : null;
}

/** A place in an area that has a link in its sidebar: one of its lists, or one of its pages. */
export interface AreaDestination {
    key: string;
    label: string;
    path: string;
}

/**
 * Every list and page in an area, lists first, in sidebar order. Cmd+K's
 * "Go to" commands and the area's remembered spot are both read from this,
 * so a page cannot have a command that the remembered spot refuses.
 */
export function areaDestinations(area: Area): AreaDestination[] {
    return [
        ...area.entities.map((entity) => ({ key: `entity-${entity.entityId}`, label: entity.label, path: listPathFor(entity.entityId) })),
        ...area.pages.map((page) => ({ key: `page-${page.routeName}`, label: page.label, path: page.path })),
    ];
}

/** The record address, for example `/clients/zones/61`. */
export function recordPathFor(entityId: string, recordId: number | string): string {
    return `${listPathFor(entityId)}/${recordId}`;
}


/**
 * The areas this user may use, each narrowed to the entities and pages they may
 * open. Home is always there. Any other area with nothing left is dropped, so its
 * button never shows in the strip.
 */
export function visibleAreas(gateIsOpen: (gate: Gate) => boolean): Area[] {
    return AREAS.map((area) => ({
        ...area,
        entities: area.entities.filter((entity) => gateIsOpen(entityGate(entity.entityId))),
        pages: area.pages.filter((page) => page.gate === null || gateIsOpen(page.gate)),
    })).filter((area) => area.id === 'home' || area.entities.length > 0 || area.pages.length > 0);
}
